/**
 * Wandaufbau-Schnittzeichnungen der Bauartklassen — die großen Kartenbilder.
 *
 * Die Haus-Silhouetten aus `bauartIcons.tsx` zeigten das, worin sich die
 * Klassen gerade NICHT unterscheiden: von außen sind FHG I, II und III
 * dasselbe Haus. Getrennt werden sie am **Wandaufbau**, deshalb zeigt jede
 * Karte einen Querschnitt.
 *
 * **Leserichtung.** Drei gestapelte Zonen: Dachzone oben, Wandschnitt in der
 * Mitte, Sockel/Gründung unten. Im Wandschnitt läuft die **Wanddicke
 * waagerecht** (links außen → rechts innen), die Schichten stehen also als
 * senkrechte Streifen nebeneinander und lassen sich abzählen. Die Wandhöhe
 * läuft senkrecht: Rähm und Schwelle eines Ständerwerks liegen in der
 * Schnittebene und erscheinen als geschnittene Holzquerschnitte oben und
 * unten. Der senkrechte Ständer liegt **nicht** in der Schnittebene und wird
 * bewusst nicht gezeichnet — er wäre in dieser Projektion eine Erfindung; die
 * Ständerwerk-Aussage tragen Rähm, Schwelle und der gedämmte Hohlraum.
 *
 * **Gemeinsames Raster.** Dachüberstand (x 12…108), Sockel (x 20…100) und die
 * fünf Schichtspuren über die Wanddicke sind bei allen sechs Signaturen
 * identisch; nur die Wand selbst variiert. Damit ist ihre Dicke am konstanten
 * Sockel ablesbar, die Karten springen nebeneinander nicht, und eine fehlende
 * Schicht bleibt als leere (gestrichelte) Spur an derselben x-Position stehen.
 * FHG I/II/III trennen sich so über die abzählbare Zahl der Platten
 * (4 / 1 / 0) statt über Farbnuancen — ein Teil der Nutzer sieht Farben
 * eingeschränkt.
 *
 * Bewusst **ohne** `<pattern>`/`<defs>`: alle Texturen sind ausgerechnete
 * Pfade. Damit gibt es keine `url(#…)`-IDs, keine Kollision zwischen zwei
 * Pickern im selben Baum (React `useId` liefert Doppelpunkte, die in `url(#…)`
 * brechen) und kein WebKit-Problem mit Paint-Servern.
 *
 * Die alten Haus-Silhouetten (`bauartIcons.tsx`) sind mit diesem Umbau
 * entfallen — sie hatten außerhalb des Pickers keinen Aufrufer.
 */
import { ILLU } from "./illustrationTokens";
import type { DachSignatur, WandSignatur } from "./illustrationTokens";

/* ------------------------------------------------------------------ Raster --- */

/** Oberkante der Wand = Auflager des Daches. */
const WAND_OBEN = 36;
/** Unterkante der Wand = Oberkante des Sockels. */
const WAND_UNTEN = 92;
const WAND_HOEHE = WAND_UNTEN - WAND_OBEN;

/**
 * Schichtspuren über die Wanddicke, außen (links) → innen (rechts). Für ALLE
 * Signaturen gleich: nur so steht eine weggelassene Schicht bei FHG II/III an
 * derselben Stelle wie die vorhandene bei FHG I.
 */
const SPUR = {
  /** Äußere Beplankung, 2. Lage — nur FHG I. */
  a: 24,
  /** Äußere Beplankung, 1. Lage — FHG I und FHG II (die Ummantelung). */
  b: 34,
  /** Kern: Rähm/Schwelle mit gedämmtem Gefach dazwischen. */
  kern: 44,
  /** Innere Beplankung, 1. Lage — nur FHG I. */
  c: 76,
  /** Innere Beplankung, 2. Lage — nur FHG I. */
  d: 86,
} as const;
/** Breite einer Beplankungsspur. */
const SPUR_BREITE = 10;
/** Breite des Kerns (Ständerwerk bzw. Glasfeld). */
const KERN_BREITE = SPUR.c - SPUR.kern;

/* -------------------------------------------------------------- Dachzone --- */

/** Eine Ziegelkante als Reihe gleichsinniger Bögen — „geschuppt", nicht gewellt. */
const ziegelKante = (y: number) => `M12 ${y}${"q3 1.8 6 0".repeat(16)}`;

const ZIEGEL_STOSS = (() => {
  const teile: string[] = [];
  for (let x = 18; x <= 102; x += 12) teile.push(`M${x} 22v4.4`);
  for (let x = 24; x <= 96; x += 12) teile.push(`M${x} 26.8v4.2`);
  for (let x = 18; x <= 102; x += 12) teile.push(`M${x} 31.4v4.4`);
  return teile.join("");
})();

/**
 * Harte Dachung — erkennbar an der dünnen, flach anliegenden Deckung aus drei
 * geschuppten Ziegelreihen mit scharfer, gerader Traufkante.
 */
const DachHart = () => (
  <g>
    <rect x={12} y={22} width={96} height={14} fill={ILLU.roofFill} />
    <path d={`${ziegelKante(26.6)}${ziegelKante(31.2)}`} stroke={ILLU.roofStroke} strokeWidth={1} fill="none" />
    <path d={ZIEGEL_STOSS} stroke={ILLU.roofStroke} strokeWidth={0.9} fill="none" />
    <rect x={12} y={22} width={96} height={14} fill="none" stroke={ILLU.roofStroke} strokeWidth={1.6} />
    <path d="M8 36h104" stroke={ILLU.roofStroke} strokeWidth={2.4} strokeLinecap="round" fill="none" />
  </g>
);

/**
 * Umriss der Reetdeckung: senkrechte Stirnseiten (die Deckung ist auch an der
 * Traufe dick), gewölbter First, achtfach ausgerundete Traufkante. Die
 * senkrechten Enden sind wichtig — ohne sie liest sich die Form als Kappe
 * statt als dicke Deckschicht.
 */
const REET_UMRISS = `M12 33V24C12 13 26 8 42 8H78C94 8 108 13 108 24V33${"c-3 4.2-9 4.2-12 0".repeat(8)}Z`;

const REET_HALME = (() => {
  const teile: string[] = [];
  for (let x = 20; x <= 100; x += 5) teile.push(`M${x.toFixed(1)} ${x < 26 || x > 94 ? 22 : 15}L${(x - 2).toFixed(1)} 31`);
  return teile.join("");
})();

/**
 * Weiche Dachung — erkennbar an der ungleich dickeren, faserigen Deckung: sie
 * füllt die halbe Bildhöhe, hat einen gewölbten First und eine wulstig
 * ausgerundete Traufe statt einer scharfen Kante.
 */
const DachWeich = () => (
  <g>
    <path d={REET_UMRISS} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={1.5} strokeLinejoin="round" />
    <path d={REET_HALME} stroke={ILLU.thatchTexture} strokeWidth={0.9} fill="none" />
    <path d="M40 13q20 -5 40 0" stroke={ILLU.thatchTexture} strokeWidth={2.2} fill="none" strokeLinecap="round" />
  </g>
);

const Dachzone = ({ dach }: { dach: DachSignatur }) => (dach === "weich" ? <DachWeich /> : <DachHart />);

/* ------------------------------------------------------------ Bausteine --- */

/** Geschnittenes Holzbauteil: Fläche, Kontur und zwei Faserbögen. */
const HolzQuerschnitt = ({ x, y, w, h }: { x: number; y: number; w: number; h: number }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill={ILLU.timberFill} stroke={ILLU.timberStroke} strokeWidth={1.2} />
    <path
      d={`M${x + 3} ${y + h - 2}q${w / 2 - 3} ${4 - h} ${w - 6} 0M${x + 8} ${y + h - 2}q${w / 2 - 8} ${3 - h} ${w - 16} 0`}
      stroke={ILLU.timberStroke}
      strokeWidth={0.8}
      fill="none"
    />
  </g>
);

/**
 * Eine mineralische Beplankungsschicht. Die Strichtextur liegt in `wallStroke`
 * (5,2:1 gegen `plateFill`) — `jointStroke` käme hier nur auf 2,4:1.
 */
const Platte = ({ x }: { x: number }) => {
  const textur: string[] = [];
  for (let y = WAND_OBEN + 4, reihe = 0; y < WAND_UNTEN - 2; y += 6, reihe += 1) {
    textur.push(`M${x + (reihe % 2 === 0 ? 2.2 : 5.4)} ${y}h2.6`);
  }
  return (
    <g>
      <rect x={x} y={WAND_OBEN} width={SPUR_BREITE} height={WAND_HOEHE} fill={ILLU.plateFill} />
      <path d={textur.join("")} stroke={ILLU.wallStroke} strokeWidth={1} fill="none" strokeLinecap="round" />
      <rect
        x={x}
        y={WAND_OBEN}
        width={SPUR_BREITE}
        height={WAND_HOEHE}
        fill="none"
        stroke={ILLU.wallStroke}
        strokeWidth={1.2}
      />
    </g>
  );
};

/**
 * Weggelassene Schicht: die Spur bleibt als gestrichelter Umriss stehen, damit
 * FHG I/II/III nebeneinander abzählbar sind. `jointStroke` hält gegen den
 * Bildhintergrund `headerBg` 3,37:1.
 */
const LeereSpur = ({ x }: { x: number }) => (
  <rect
    x={x + 0.5}
    y={WAND_OBEN + 0.5}
    width={SPUR_BREITE - 1}
    height={WAND_HOEHE - 1}
    fill="none"
    stroke={ILLU.jointStroke}
    strokeWidth={0.9}
    strokeDasharray="2 3"
  />
);

const DAEMM_WELLE = (() => {
  const teile: string[] = [];
  for (let y = 49; y < 83; y += 6) teile.push(`M${SPUR.kern} ${y}q8 -4 16 0t16 0`);
  return teile.join("");
})();

/**
 * Kern aller drei Fertighausgruppen: geschnittenes Rähm oben, geschnittene
 * Schwelle unten, dazwischen der gedämmte Hohlraum mit Wellenschraffur.
 * Identisch in FHG I/II/III — den Unterschied machen ausschließlich die
 * Beplankungsspuren links und rechts davon.
 */
const FertigKern = () => (
  <g>
    <rect x={SPUR.kern} y={WAND_OBEN} width={KERN_BREITE} height={WAND_HOEHE} fill={ILLU.wallFill} />
    <path d={DAEMM_WELLE} stroke={ILLU.jointStroke} strokeWidth={1.1} fill="none" />
    <HolzQuerschnitt x={SPUR.kern} y={WAND_OBEN} w={KERN_BREITE} h={8} />
    <HolzQuerschnitt x={SPUR.kern} y={WAND_UNTEN - 8} w={KERN_BREITE} h={8} />
    <rect
      x={SPUR.kern}
      y={WAND_OBEN}
      width={KERN_BREITE}
      height={WAND_HOEHE}
      fill="none"
      stroke={ILLU.timberStroke}
      strokeWidth={1.5}
    />
  </g>
);

/* ------------------------------------------------------- Wand-Signaturen --- */

const MASSIV_FUGEN = (() => {
  const teile: string[] = [];
  for (let y = WAND_OBEN + 8; y < WAND_UNTEN; y += 8) teile.push(`M24 ${y}h72`);
  let lage = 0;
  for (let y = WAND_OBEN; y < WAND_UNTEN; y += 8, lage += 1) {
    (lage % 2 === 0 ? [48, 72] : [36, 60, 84]).forEach((x) => teile.push(`M${x} ${y}v8`));
  }
  return teile.join("");
})();

/**
 * Massiv — erkennbar am einzigen, über die volle Wanddicke durchgehenden
 * Steinverband: kein Ständer, kein Hohlraum, keine aufgesetzte Schicht, und
 * die breiteste Wand im Bild.
 */
const WandMassiv = () => (
  <g>
    <rect x={SPUR.a} y={WAND_OBEN} width={72} height={WAND_HOEHE} fill={ILLU.wallFill} />
    <path d={MASSIV_FUGEN} stroke={ILLU.jointStroke} strokeWidth={1} fill="none" />
    <rect x={SPUR.a} y={WAND_OBEN} width={72} height={WAND_HOEHE} fill="none" stroke={ILLU.wallStroke} strokeWidth={1.8} />
  </g>
);

/**
 * Stahl/Glas — erkennbar am dünnsten Aufbau des Satzes: zwei haarfeine, dunkle
 * Stahlprofile fassen ein durchsichtiges Glasfeld über die volle Wanddicke,
 * dazu Stahlriegel oben und unten. Kein Holz, kein Mauerwerk.
 */
const WandStahlGlas = () => (
  <g>
    <rect x={SPUR.kern} y={WAND_OBEN} width={KERN_BREITE} height={WAND_HOEHE} fill={ILLU.glassFill} />
    {/* Sprossen in wallStroke — jointStroke hält auf glassFill nur 2,70:1. */}
    <path d={`M${SPUR.kern} 55h${KERN_BREITE}M${SPUR.kern} 73h${KERN_BREITE}`} stroke={ILLU.wallStroke} strokeWidth={1} fill="none" />
    <rect x={SPUR.kern} y={WAND_OBEN} width={4.5} height={WAND_HOEHE} fill={ILLU.steelFill} />
    <rect x={SPUR.c - 4.5} y={WAND_OBEN} width={4.5} height={WAND_HOEHE} fill={ILLU.steelFill} />
    {/* Helle Mittellinie im Profil: aus Flansch + Steg wird die doppelte Haarlinie. */}
    <path d={`M${SPUR.kern + 2.25} ${WAND_OBEN}v${WAND_HOEHE}M${SPUR.c - 2.25} ${WAND_OBEN}v${WAND_HOEHE}`} stroke={ILLU.glassFill} strokeWidth={0.9} fill="none" />
    <rect x={SPUR.kern} y={WAND_OBEN} width={KERN_BREITE} height={4.5} fill={ILLU.steelFill} />
    <rect x={SPUR.kern} y={WAND_UNTEN - 4.5} width={KERN_BREITE} height={4.5} fill={ILLU.steelFill} />
    <rect
      x={SPUR.kern}
      y={WAND_OBEN}
      width={KERN_BREITE}
      height={WAND_HOEHE}
      fill="none"
      stroke={ILLU.wallStroke}
      strokeWidth={1.2}
    />
  </g>
);

/** Andreaskreuz im Gefach — Endpunkte so gesetzt, dass die Bandbreite innen bleibt. */
const ANDREASKREUZ = "M39 79 81 49M39 49 81 79";

/**
 * Holz/Lehmfachwerk — erkennbar am Andreaskreuz: als einzige Signatur trägt
 * sie ein schräges Bauteil, und das Holz steht bündig über die volle Wanddicke
 * ohne jede Beplankung auf beiden Seiten frei.
 */
const WandHolz = () => (
  <g>
    <rect x={SPUR.b} y={WAND_OBEN} width={52} height={WAND_HOEHE} fill={ILLU.gefachFill} />
    <path d={ANDREASKREUZ} stroke={ILLU.timberStroke} strokeWidth={8} fill="none" />
    <path d={ANDREASKREUZ} stroke={ILLU.timberFill} strokeWidth={6} fill="none" />
    <HolzQuerschnitt x={SPUR.b} y={WAND_OBEN} w={52} h={9} />
    <HolzQuerschnitt x={SPUR.b} y={WAND_UNTEN - 9} w={52} h={9} />
    <rect x={SPUR.b} y={WAND_OBEN} width={52} height={WAND_HOEHE} fill="none" stroke={ILLU.timberStroke} strokeWidth={1.6} />
  </g>
);

/**
 * Fertighaus, in allen Teilen feuerbeständig (FHG I) — erkennbar an vier
 * Platten: der Kern ist beidseitig doppelt beplankt, das Holz liegt nirgends
 * frei, und es bleibt keine leere Spur übrig.
 */
const WandFertigFeuerbestaendig = () => (
  <g>
    <Platte x={SPUR.a} />
    <Platte x={SPUR.b} />
    <FertigKern />
    <Platte x={SPUR.c} />
    <Platte x={SPUR.d} />
  </g>
);

/**
 * Fertighaus, feuerhemmend ummantelt (FHG II) — erkennbar an genau einer
 * Platte außen: die zweite Außenspur und beide Innenspuren stehen leer, innen
 * liegt das Holz frei.
 */
const WandFertigUmmantelt = () => (
  <g>
    <LeereSpur x={SPUR.a} />
    <Platte x={SPUR.b} />
    <FertigKern />
    <LeereSpur x={SPUR.c} />
    <LeereSpur x={SPUR.d} />
  </g>
);

/**
 * Fertighaus ohne Ummantelung (FHG III) — erkennbar daran, dass alle vier
 * Beplankungsspuren leer bleiben: derselbe Kern wie FHG II, aber ohne jede
 * Haut, das Holz liegt auf beiden Seiten frei.
 */
const WandFertigOffen = () => (
  <g>
    <LeereSpur x={SPUR.a} />
    <LeereSpur x={SPUR.b} />
    <FertigKern />
    <LeereSpur x={SPUR.c} />
    <LeereSpur x={SPUR.d} />
  </g>
);

const Wandschnitt = ({ wand }: { wand: WandSignatur }) => {
  switch (wand) {
    case "massiv":
      return <WandMassiv />;
    case "stahlglas":
      return <WandStahlGlas />;
    case "holz":
      return <WandHolz />;
    case "fertigFeuerbestaendig":
      return <WandFertigFeuerbestaendig />;
    case "fertigUmmantelt":
      return <WandFertigUmmantelt />;
    case "fertigOffen":
    default:
      return <WandFertigOffen />;
  }
};

/* ---------------------------------------------------------------- Sockel --- */

const SOCKEL_SCHRAFFUR = (() => {
  const teile: string[] = [];
  for (let x = 24; x <= 88; x += 8) teile.push(`M${x} 104l8 -10`);
  return teile.join("");
})();

/**
 * Sockel/Gründung — bei allen sechs Schnitten identisch. Genau deshalb ist er
 * da: an seiner konstanten Breite lässt sich die Wanddicke ablesen.
 */
const Sockel = () => (
  <g>
    <rect x={20} y={92} width={80} height={14} fill={ILLU.sockelFill} />
    <path d={SOCKEL_SCHRAFFUR} stroke={ILLU.wallStroke} strokeWidth={1} fill="none" />
    <rect x={20} y={92} width={80} height={14} fill="none" stroke={ILLU.wallStroke} strokeWidth={1.4} />
    <path d="M8 106h104" stroke={ILLU.wallStroke} strokeWidth={2.2} strokeLinecap="round" fill="none" />
    <path
      d="M14 113 20 106M26 113 32 106M38 113 44 106M50 113 56 106M62 113 68 106M74 113 80 106M86 113 92 106M98 113 104 106"
      stroke={ILLU.jointStroke}
      strokeWidth={1}
      fill="none"
    />
  </g>
);

/* ------------------------------------------------------------ Wortlaute --- */

/**
 * Wortlaute zu jeder Zone. `kurz` sind die Beschriftungen **neben** der
 * Zeichnung — echter HTML-Text, den die Karte rendert, damit er auswählbar und
 * vom Screenreader lesbar ist und auf schmalen Spalten weichen darf. `lang`
 * geht in `<title>`/`aria-label`, damit die Zeichnung nicht die einzige
 * Trägerin der Aussage ist.
 */
const DACH_WORTE: Record<DachSignatur, { kurz: string; lang: string }> = {
  hart: { kurz: "Ziegel, geschuppt", lang: "harte Dachung als dünne, geschuppte Ziegeldeckung" },
  weich: { kurz: "Reet, dicke Schicht", lang: "weiche Dachung als dicke, faserige Reetschicht" },
};

/**
 * Die Wand-Wortlaute nennen bei den Fertighausgruppen bewusst die **Zahl** der
 * Platten: damit steht das Unterscheidungsmerkmal auch in Worten da und hängt
 * nicht am Bild allein.
 */
const WAND_WORTE: Record<WandSignatur, { kurz: string; lang: string }> = {
  massiv: {
    kurz: "Mauerwerk, volle Tiefe",
    lang: "massives Mauerwerk über die volle Wanddicke, ohne Hohlraum und ohne Ständerwerk",
  },
  stahlglas: {
    kurz: "Stahlprofil + Glas",
    lang: "dünner Aufbau aus zwei schlanken Stahlprofilen mit Glasfeld dazwischen",
  },
  holz: {
    kurz: "Holzständer + Gefach",
    lang: "Holzständerwerk mit bündigem Gefach und sichtbarem Andreaskreuz, Holz auf beiden Seiten offen",
  },
  fertigFeuerbestaendig: {
    kurz: "4 Platten, beidseitig",
    lang: "Ständerwerk mit Dämmung, beidseitig doppelt mit mineralischen Platten beplankt — vier Platten",
  },
  fertigUmmantelt: {
    kurz: "1 Platte, nur außen",
    lang: "Ständerwerk mit Dämmung und genau einer Ummantelungsplatte außen, innen liegt das Holz frei",
  },
  fertigOffen: {
    kurz: "keine Platte, Holz frei",
    lang: "Ständerwerk mit Dämmung ohne jede Beplankung, das Holz liegt auf beiden Seiten frei",
  },
};

/** Wortlaut für `<title>`/`aria-label` — nennt den Aufbau von außen nach innen. */
export const schnittBeschreibung = (wand: WandSignatur, dach: DachSignatur): string =>
  `Querschnitt von außen nach innen: ${DACH_WORTE[dach].lang}, ${WAND_WORTE[wand].lang}, Sockel auf der Gründung.`;

/**
 * Die zwei bis drei kurzen Beschriftungen neben der Zeichnung, von oben nach
 * unten (Dachzone, Wandschnitt, Sockel). Sie benennen, was man sieht, und
 * wiederholen bewusst nicht den Klartext der Karte aus `enums.ts`.
 */
export const schnittBeschriftungen = (
  wand: WandSignatur,
  dach: DachSignatur,
): readonly [string, string, string] => [DACH_WORTE[dach].kurz, WAND_WORTE[wand].kurz, "Sockel"];

/* ------------------------------------------------------------ Komponente --- */

type Props = {
  wand: WandSignatur;
  dach: DachSignatur;
  /** Maße kommen per CSS (Griffel); width/height sind nur der Fallback. */
  className?: string;
  /**
   * Nimmt die Zeichnung aus dem Barrierefreiheits-Baum. Zu setzen überall dort,
   * wo direkt daneben schon Text steht, der dasselbe sagt — im Picker sind das
   * die drei Kurzbeschriftungen und der Klartext der Karte.
   *
   * Der Grund ist der Vorlesename: die Karte ist ein `role="radio"`, ihr Name
   * entsteht aus dem Inhalt, und ein `role="img"` mit `aria-label` steuert
   * seinen ganzen Namen bei. Gemessen waren das ~300 Zeichen je Karte, also
   * ~2400 für einen Pfeiltasten-Durchlauf durch die acht Optionen — und zwar
   * inhaltlich dasselbe, was daneben schon als sichtbarer Text steht. Ein Bild,
   * das nur wiederholt, was daneben steht, ist dekorativ; so hielt es auch die
   * abgelöste `bauartIcons.tsx` („Rein dekorativ: die fachliche Aussage steht
   * als Text in der Karte") und so hält es `DachZoneIcon` unten.
   *
   * Das `<title>` bleibt in beiden Fällen stehen: es ist der Tooltip beim
   * Hovern und liegt unter `aria-hidden` nicht mehr im Namen.
   */
  dekorativ?: boolean;
};

/**
 * Der große Kartenbaustein: Dachzone, Wandschnitt und Sockel in einer
 * quadratischen viewBox. `width`/`height` sind bewusst 160 statt der alten
 * 100x80 — unter etwa 120 px Kantenlänge verlieren Plattentextur und
 * Dämmschraffur ihre Lesbarkeit.
 */
export const BauartSchnitt = ({ wand, dach, className, dekorativ = false }: Props) => {
  const beschreibung = schnittBeschreibung(wand, dach);
  return (
    <svg
      className={className}
      width="160"
      height="160"
      viewBox="0 0 120 120"
      fill="none"
      // Entweder Bild MIT Namen oder gar kein Eintrag im Baum — `role="img"`
      // und `aria-hidden` zugleich wäre widersprüchlich.
      role={dekorativ ? undefined : "img"}
      aria-label={dekorativ ? undefined : beschreibung}
      aria-hidden={dekorativ ? true : undefined}
      focusable="false"
    >
      {/* Tooltip beim Hovern — und ohne `dekorativ` zugleich der Bildname. */}
      <title>{beschreibung}</title>
      <rect width="120" height="120" fill={ILLU.headerBg} />
      <Sockel />
      <Wandschnitt wand={wand} />
      {/* Zuletzt, damit die Reetdeckung den Wandkopf überdeckt statt umgekehrt. */}
      <Dachzone dach={dach} />
    </svg>
  );
};

/**
 * Kleine Fassung nur der Dachzone — für die Legende „harte/weiche Dachung".
 * Gleiche viewBox-Ausschnitthöhe für beide Zustände, damit der Dickenunterschied
 * erhalten bleibt (er ist die ganze Aussage). `aria-hidden`, weil der
 * Legendentext direkt daneben dasselbe sagt und die Doppelansage stört.
 */
export const DachZoneIcon = ({ dach }: { dach: DachSignatur }) => (
  <svg width="64" height="21" viewBox="8 4 104 34" fill="none" aria-hidden="true" focusable="false">
    <Dachzone dach={dach} />
  </svg>
);
